import { BrandMark } from '@/components/BrandMark';
import { Phone, Mail, MapPin, ArrowRight, Instagram, Facebook, Linkedin, Youtube, Twitter } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSupportContact, buildWhatsAppUrl, formatBrPhone } from '@/hooks/useSupportContact';

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
      <path d="M19.11 17.39c-.27-.14-1.61-.79-1.86-.88-.25-.09-.43-.14-.61.14-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.14-1.14-.42-2.17-1.34-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.42.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.61-1.47-.84-2.02-.22-.53-.45-.46-.61-.47h-.52c-.18 0-.48.07-.73.34-.25.27-.95.93-.95 2.27 0 1.34.98 2.64 1.11 2.82.14.18 1.92 2.93 4.66 4.11.65.28 1.16.45 1.56.58.65.21 1.25.18 1.72.11.52-.08 1.61-.66 1.84-1.29.23-.64.23-1.18.16-1.29-.07-.11-.25-.18-.52-.32zM16 4C9.37 4 4 9.37 4 16c0 2.11.55 4.16 1.6 5.97L4 28l6.2-1.62A11.94 11.94 0 0 0 16 28c6.63 0 12-5.37 12-12S22.63 4 16 4zm0 21.82a9.8 9.8 0 0 1-5.01-1.37l-.36-.21-3.68.96.98-3.59-.23-.37A9.82 9.82 0 1 1 25.82 16 9.83 9.83 0 0 1 16 25.82z"/>
    </svg>
  );
}

const PRODUCT_LINKS = [
  { label: 'Dashboard', to: '/app' },
  { label: 'Conversores', to: '/conversores' },
  
  { label: 'Assinatura', to: '/planos' },
  { label: 'Calculadora de Economia', to: '/economia-de-tempo' },
  { label: 'Planos', to: '/planos' },
];

const CONVERTER_LINKS = [
  { label: 'PDF para Excel', to: '/pdf-para-excel' },
  { label: 'PDF para Word', to: '/pdf-para-word' },
  { label: 'XML para PDF', to: '/xml-para-pdf' },
  { label: 'XML para Excel', to: '/xml-para-excel' },
  { label: 'OCR Inteligente', to: '/ocr-inteligente' },
  { label: 'Excel para PDF', to: '/excel-para-pdf' },
];

const COMPANY_LINKS = [
  { label: 'Sobre a FiscalOnne', to: '/' },
  { label: 'Ajuda / FAQ', to: '/ajuda' },
  { label: 'Contato', to: '/ajuda#contato' },
  { label: 'Reportar erro', to: '/ajuda#contato' },
  { label: 'Política de Privacidade', to: '/sitemap#privacidade' },
  { label: 'Termos de Uso', to: '/sitemap#termos' },
  { label: 'Sitemap', to: '/sitemap' },
];

export function Footer() {
  const { contact } = useSupportContact();
  return (
    <footer className="bg-[#050A0F] border-t border-white/5 mt-auto relative z-0">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* CTAs */}
        <div className="flex flex-wrap items-center justify-center gap-3 pb-10 mb-10 border-b border-white/5">
          <Link to="/economia-de-tempo" className="text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-lg border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 transition-colors inline-flex items-center gap-2">
            Calcular minha economia <ArrowRight className="w-3 h-3" />
          </Link>
          <Link to="/conversores" className="text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 transition-colors inline-flex items-center gap-2">
            Testar conversor gratuito <ArrowRight className="w-3 h-3" />
          </Link>
          <Link to="/planos" className="text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-lg bg-white text-black hover:bg-slate-200 transition-colors inline-flex items-center gap-2">
            Ver planos <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <BrandMark size={36} glow="none" />

              <span className="text-base font-bold text-white">
                Fiscal<span className="text-cyan-500">Onne</span>
              </span>
            </Link>
            <p className="text-xs text-slate-400 leading-relaxed mb-4">
              Automação fiscal para organizar XMLs, gerar PDFs, converter documentos e economizar tempo com notas fiscais.
            </p>
            <ul className="space-y-2 text-xs text-slate-400">
              {contact.cnpj && <li className="font-mono opacity-70">CNPJ: {contact.cnpj}</li>}
              {contact.address && (
                <li className="inline-flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" /> {contact.address}
                </li>
              )}
              <li>
                <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-2 hover:text-cyan-400 transition-colors">
                  <Mail className="w-3.5 h-3.5" /> {contact.email}
                </a>
              </li>
              {contact.phone && (
                <li>
                  <a href={`tel:+${contact.phone}`} className="inline-flex items-center gap-2 hover:text-cyan-400 transition-colors">
                    <Phone className="w-3.5 h-3.5" /> {formatBrPhone(contact.phone)}
                  </a>
                </li>
              )}
              {contact.whatsapp && (
                <li>
                  <a
                    href={buildWhatsAppUrl(contact.whatsapp)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                  >
                    <WhatsAppIcon className="w-4 h-4" /> WhatsApp {formatBrPhone(contact.whatsapp)}
                  </a>
                </li>
              )}
            </ul>

            <SocialLinks contact={contact} />
          </div>

          {/* Produto */}
          <FooterColumn title="Produto" links={PRODUCT_LINKS} />
          {/* Conversores */}
          <FooterColumn title="Conversores" links={CONVERTER_LINKS} />
          {/* Empresa */}
          <FooterColumn title="Empresa" links={COMPANY_LINKS} />
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} FiscalOnne. Todos os direitos reservados.</p>
          <div className="flex items-center gap-4">
            <Link to="/sitemap" className="hover:text-cyan-400 transition-colors">Sitemap</Link>
            <a href="/sitemap.xml" className="hover:text-cyan-400 transition-colors">Sitemap XML</a>
            <span className="tracking-widest uppercase text-[10px] text-white/40">
              Desenvolvido por <span className="text-cyan-500">NILL COMPANY</span>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }: { title: string; links: { label: string; to: string }[] }) {
  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-widest text-white mb-4">{title}</h3>
      <ul className="space-y-2.5">
        {links.map((l) => (
          <li key={l.label}>
            <Link to={l.to} className="text-xs text-slate-400 hover:text-cyan-400 transition-colors">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialLinks({ contact }: { contact: { instagram?: string; facebook?: string; linkedin?: string; youtube?: string; x?: string } }) {
  const items = [
    { url: contact.instagram, Icon: Instagram, label: 'Instagram' },
    { url: contact.facebook, Icon: Facebook, label: 'Facebook' },
    { url: contact.linkedin, Icon: Linkedin, label: 'LinkedIn' },
    { url: contact.youtube, Icon: Youtube, label: 'YouTube' },
    { url: contact.x, Icon: Twitter, label: 'X' },
  ].filter((i) => !!i.url);

  if (items.length === 0) return null;

  return (
    <div className="mt-5 flex items-center gap-2">
      {items.map(({ url, Icon, label }) => (
        <a
          key={label}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className="w-8 h-8 rounded-lg border border-white/10 text-slate-400 hover:text-cyan-400 hover:border-cyan-500/40 flex items-center justify-center transition-colors"
        >
          <Icon className="w-4 h-4" />
        </a>
      ))}
    </div>
  );
}
